import { v4 as uuidv4 } from 'uuid';

/**
 * PSTUDIO-3671: live cross-(browser-)tab solution-lifecycle signal. Separate
 * from the node-scoped `ps-solution-nodes` channel: this one carries a
 * "solution deleted" event so a sibling tab with the same solution open can
 * eject immediately (toast + redirect) without waiting for a reload. Same-
 * browser only — cross-machine is covered by the backend-anchored detection in
 * the load flow.
 */
export const SOLUTION_SYNC_CHANNEL_NAME = 'ps-solution-lifecycle';

/** Identifies THIS tab so a subscriber never reacts to its own broadcasts. */
export const SOLUTION_SYNC_TAB_ID = uuidv4();

export interface SolutionDeletedMessage {
  projectId: string;
  botName: string;
}

interface WireMessage extends SolutionDeletedMessage {
  sourceTabId: string;
}

function isBroadcastSupported(): boolean {
  return typeof BroadcastChannel !== 'undefined';
}

let publishChannel: BroadcastChannel | null = null;

/** Post "this solution was deleted" to sibling tabs. Fire-and-forget — an
 *  unavailable channel must never break the delete path. */
export function broadcastSolutionDeleted(msg: SolutionDeletedMessage): void {
  if (!isBroadcastSupported() || !msg.projectId || !msg.botName) return;
  try {
    if (!publishChannel) {
      publishChannel = new BroadcastChannel(SOLUTION_SYNC_CHANNEL_NAME);
      // Node's BroadcastChannel (a global since Node 18 — i.e. in vitest
      // env=node) keeps the event loop alive unless unref'd, which would hang
      // `vitest run` at exit. Browsers have no unref — optional call is a no-op.
      (publishChannel as unknown as { unref?: () => void }).unref?.();
    }
    const wire: WireMessage = { ...msg, sourceTabId: SOLUTION_SYNC_TAB_ID };
    publishChannel.postMessage(wire);
  } catch {
    publishChannel = null;
  }
}

/** Subscribe to sibling-tab solution-deleted events. Returns an unsubscribe fn. */
export function subscribeToSolutionDeleted(
  onMessage: (msg: SolutionDeletedMessage) => void
): () => void {
  if (!isBroadcastSupported()) return () => undefined;
  const channel = new BroadcastChannel(SOLUTION_SYNC_CHANNEL_NAME);
  const handler = (event: MessageEvent) => {
    const msg = event.data as Partial<WireMessage> | null | undefined;
    if (!msg?.projectId || !msg.botName || !msg.sourceTabId) return;
    if (msg.sourceTabId === SOLUTION_SYNC_TAB_ID) return; // own broadcast
    onMessage({ projectId: msg.projectId, botName: msg.botName });
  };
  channel.addEventListener('message', handler);
  return () => {
    channel.removeEventListener('message', handler);
    channel.close();
  };
}
